'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdMedication, MdSave, MdArrowBack, MdAdd, MdDelete } from 'react-icons/md'
import {
    getPrescription,
    createPrescription,
    CreatePrescriptionData,
    CreatePrescriptionItemData,
    AdministrationRoute,
    ADMINISTRATION_ROUTE_LABELS,
} from '@/lib/api/hospital'
import { getAdmissions, Admission } from '@/lib/api/hospital'

const defaultItem: CreatePrescriptionItemData = {
    medicationName: '',
    dose: '',
    unit: 'mg',
    route: 'ORAL',
    frequency: '',
    scheduleType: 'FIXED',
    ifNeeded: false,
    isAntibiotic: false,
    isContinuous: false,
    requiresDoubleCheck: false,
}

export default function PrescriptionFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = Boolean(id)

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [admissions, setAdmissions] = useState<Admission[]>([])
    const [formData, setFormData] = useState<Omit<CreatePrescriptionData, 'items'>>({
        admissionId: 0,
        validFrom: new Date().toISOString().split('T')[0],
        validUntil: '',
        notes: '',
    })
    const [items, setItems] = useState<CreatePrescriptionItemData[]>([{ ...defaultItem }])

    useEffect(() => {
        loadAdmissions()
        if (isEditing && id) {
            loadPrescription(parseInt(id))
        }
    }, [id, isEditing])

    const loadAdmissions = async () => {
        try {
            const response = await getAdmissions(undefined, { status: 'ADM_ACTIVE' })
            if (response.success) {
                setAdmissions(response.result)
            }
        } catch (error) {
            console.error('Error loading admissions:', error)
        }
    }

    const loadPrescription = async (prescriptionId: number) => {
        setLoading(true)
        try {
            const response = await getPrescription(prescriptionId)
            if (response.success) {
                const presc = response.result
                setFormData({
                    admissionId: presc.admissionId,
                    validFrom: presc.validFrom.split('T')[0],
                    validUntil: presc.validUntil?.split('T')[0] || '',
                    notes: presc.notes || '',
                })
                if (presc.items && presc.items.length > 0) {
                    setItems(
                        presc.items.map(item => ({
                            medicationName: item.medicationName,
                            dose: item.dose,
                            unit: item.unit,
                            route: item.route,
                            frequency: item.frequency,
                            scheduleType: item.scheduleType,
                            instructions: item.instructions || '',
                            ifNeeded: item.ifNeeded,
                            ifNeededCondition: item.ifNeededCondition || '',
                            isAntibiotic: item.isAntibiotic,
                            isContinuous: item.isContinuous,
                            requiresDoubleCheck: item.requiresDoubleCheck,
                        }))
                    )
                }
            } else {
                toast.error(response.error || 'Erro ao carregar prescrição')
                navigate('/hospital/prescricoes')
            }
        } catch (error) {
            toast.error('Erro ao carregar prescrição')
            navigate('/hospital/prescricoes')
        } finally {
            setLoading(false)
        }
    }

    const addItem = () => {
        setItems([...items, { ...defaultItem }])
    }

    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index))
        }
    }

    const updateItem = (index: number, field: keyof CreatePrescriptionItemData, value: any) => {
        const newItems = [...items]
        newItems[index] = { ...newItems[index], [field]: value }
        setItems(newItems)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.admissionId) {
            toast.error('Selecione uma internação')
            return
        }

        if (items.some(item => !item.medicationName || !item.dose || !item.frequency)) {
            toast.error('Preencha todos os campos obrigatórios dos itens')
            return
        }

        setSaving(true)
        try {
            const data: CreatePrescriptionData = {
                ...formData,
                validUntil: formData.validUntil || undefined,
                notes: formData.notes || undefined,
                items,
            }

            const response = await createPrescription(data)

            if (response.success) {
                toast.success('Prescrição criada com sucesso')
                navigate('/hospital/prescricoes')
            } else {
                toast.error(response.error || 'Erro ao criar prescrição')
            }
        } catch (error) {
            toast.error('Erro ao criar prescrição')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hospital/prescricoes')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <MdMedication className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Prescrição' : 'Nova Prescrição'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize a prescrição médica'
                                : 'Crie uma nova prescrição médica'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Informações Gerais</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">Internação *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.admissionId}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        admissionId: parseInt(e.target.value),
                                    })
                                }
                                required
                            >
                                <option value={0}>Selecione uma internação</option>
                                {admissions.map(adm => (
                                    <option key={adm.id} value={adm.id}>
                                        {adm.client?.name} - Leito {adm.bed?.number || 'S/N'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Válido a partir de</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={formData.validFrom}
                                onChange={e =>
                                    setFormData({ ...formData, validFrom: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Válido até</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={formData.validUntil}
                                onChange={e =>
                                    setFormData({ ...formData, validUntil: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">Observações</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Observações gerais..."
                                value={formData.notes}
                                onChange={e =>
                                    setFormData({ ...formData, notes: e.target.value })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-lg">Itens da Prescrição</h3>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm gap-1"
                            onClick={addItem}
                        >
                            <MdAdd className="text-lg" />
                            Adicionar Item
                        </button>
                    </div>

                    <div className="space-y-6">
                        {items.map((item, index) => (
                            <div
                                key={index}
                                className="border border-base-300 rounded-lg p-4 relative"
                            >
                                {items.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm btn-circle absolute top-2 right-2"
                                        onClick={() => removeItem(index)}
                                    >
                                        <MdDelete className="text-lg text-error" />
                                    </button>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="form-control w-full md:col-span-2">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Medicamento *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Nome do medicamento"
                                            value={item.medicationName}
                                            onChange={e =>
                                                updateItem(
                                                    index,
                                                    'medicationName',
                                                    e.target.value
                                                )
                                            }
                                            required
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">Dose *</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Ex: 500"
                                            value={item.dose}
                                            onChange={e =>
                                                updateItem(index, 'dose', e.target.value)
                                            }
                                            required
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">Unidade</span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={item.unit}
                                            onChange={e =>
                                                updateItem(index, 'unit', e.target.value)
                                            }
                                        >
                                            <option value="mg">mg</option>
                                            <option value="g">g</option>
                                            <option value="mcg">mcg</option>
                                            <option value="ml">ml</option>
                                            <option value="UI">UI</option>
                                            <option value="gotas">gotas</option>
                                        </select>
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">Via *</span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={item.route}
                                            onChange={e =>
                                                updateItem(
                                                    index,
                                                    'route',
                                                    e.target.value as AdministrationRoute
                                                )
                                            }
                                            required
                                        >
                                            {Object.entries(ADMINISTRATION_ROUTE_LABELS).map(
                                                ([key, label]) => (
                                                    <option key={key} value={key}>
                                                        {label}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Frequência *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Ex: 8/8h"
                                            value={item.frequency}
                                            onChange={e =>
                                                updateItem(index, 'frequency', e.target.value)
                                            }
                                            required
                                        />
                                    </div>

                                    <div className="form-control w-full md:col-span-2">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Instruções
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Instruções especiais"
                                            value={item.instructions || ''}
                                            onChange={e =>
                                                updateItem(index, 'instructions', e.target.value)
                                            }
                                        />
                                    </div>

                                    <div className="form-control w-full md:col-span-4">
                                        <div className="flex flex-wrap gap-4">
                                            <label className="label cursor-pointer gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm"
                                                    checked={item.ifNeeded}
                                                    onChange={e =>
                                                        updateItem(
                                                            index,
                                                            'ifNeeded',
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                                <span className="label-text">Se necessário</span>
                                            </label>

                                            <label className="label cursor-pointer gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm"
                                                    checked={item.isAntibiotic}
                                                    onChange={e =>
                                                        updateItem(
                                                            index,
                                                            'isAntibiotic',
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                                <span className="label-text">Antibiótico</span>
                                            </label>

                                            <label className="label cursor-pointer gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm"
                                                    checked={item.isContinuous}
                                                    onChange={e =>
                                                        updateItem(
                                                            index,
                                                            'isContinuous',
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                                <span className="label-text">Contínuo</span>
                                            </label>

                                            <label className="label cursor-pointer gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm"
                                                    checked={item.requiresDoubleCheck}
                                                    onChange={e =>
                                                        updateItem(
                                                            index,
                                                            'requiresDoubleCheck',
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                                <span className="label-text">Dupla checagem</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/hospital/prescricoes')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        Criar Prescrição
                    </button>
                </div>
            </form>
        </div>
    )
}
